/**
 * RFC 7807 ProblemDetails, plus the `messageKey` this API adds to every failure.
 *
 * `messageKey` is the load-bearing field. The server already resolves `detail` into the user's
 * language from its own resource files, and it deliberately orders competing failures
 * most-fixable-first — subscription problems before plan problems before permissions — because a
 * request can fail several ways at once and "you do not have permission" sends the customer to an
 * administrator who will find nothing wrong.
 *
 * So the client shows `detail` and *branches* on `messageKey`. Inventing our own wording here would
 * re-create the confusion the server's ordering exists to prevent, and would break rule 13 besides.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  messageKey?: string;
  errors?: Record<string, string[]>;
}

/**
 * A failed API call.
 *
 * Thrown rather than returned so that a caller who forgets to check cannot proceed on a value that
 * was never fetched — the failure mode where a screen renders confidently against nothing.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly messageKey: string | undefined;
  readonly problem: ProblemDetails | undefined;

  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.messageKey = problem?.messageKey;
    this.problem = problem;
  }

  /** The session is gone or was never there. Signing in again is the fix. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * Refused. Note this covers three quite different situations, which
   * {@link ApiError.refusalKind} separates — the status alone is not enough to tell a user what
   * to do about it.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** A rule about existing state, e.g. a plan limit or a duplicate code. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /**
   * Why the call was refused, in terms the customer can act on.
   *
   * The four have different audiences, which is the whole reason the server keeps them apart:
   * `subscription` is for whoever pays the invoices, `plan` is a sales conversation, `limit` is
   * something their own administrator can free up, and `permission` is that administrator too but
   * for a different reason. Collapsing them into "forbidden" produces a support call about the
   * wrong thing entirely.
   */
  get refusalKind(): 'subscription' | 'plan' | 'limit' | 'permission' | 'other' {
    switch (this.messageKey) {
      // The fix is an upgrade.
      case 'Subscription_FeatureNotInPlan':
        return 'plan';

      // The fix is a payment.
      case 'Subscription_ReadOnly':
      case 'Subscription_Inactive':
        return 'subscription';

      // The fix is freeing a seat, or an upgrade — the message names the cap either way.
      case 'Subscription_UserLimitReached':
      case 'Subscription_BranchLimitReached':
      case 'Subscription_PlanLimitReached':
        return 'limit';

      case 'Auth_PermissionDenied':
        return 'permission';

      default:
        return 'other';
    }
  }
}

/**
 * A network or CORS failure — the request never reached the API, so there is no status and no
 * ProblemDetails to read. Distinct from an `ApiError` because the advice differs: retrying is
 * reasonable here and pointless for a 403.
 */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('The server could not be reached.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * Nothing answered, or what answered was not the application.
 *
 * A {@link NetworkError} (offline, DNS, CORS, a connection refused) and a 5xx are different events
 * with the same meaning for the user: the API is not there right now, and nothing they type will
 * change that. Everything below 500 is excluded deliberately — a 401, a 403 or a 409 is the API
 * working correctly and refusing something, which is a message on the screen the user is on, not a
 * reason to take the application away from them.
 *
 * 501 is the exception that proves it: "not implemented" is a deployment that answered and said no,
 * so retrying is pointless. The rest of the 5xx range — 500, 502, 503, 504 — is worth a retry.
 */
export function isUnreachable(error: unknown): boolean {
  if (error instanceof NetworkError) {
    return true;
  }

  return error instanceof ApiError && error.status >= 500 && error.status !== 501;
}

/**
 * Turns a failed response into an {@link ApiError}.
 *
 * Tolerates a body that is not ProblemDetails at all: a proxy timing out, a 502 from a load
 * balancer, or an empty 500 all arrive here, and the client must degrade to the status rather than
 * throw while handling a throw.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  let problem: ProblemDetails | undefined;

  try {
    const text = await response.text();

    if (text) {
      const parsed: unknown = JSON.parse(text);

      if (parsed !== null && typeof parsed === 'object') {
        // Every field is optional, so any object is a structurally valid ProblemDetails — which is
        // the point: an unexpected shape degrades to "no messageKey" rather than throwing.
        problem = parsed;
      }
    }
  } catch {
    // Not JSON, or not readable. The status below is still worth reporting.
  }

  const message =
    problem?.detail ?? problem?.title ?? `Request failed with status ${response.status}.`;

  return new ApiError(response.status, message, problem);
}
